import { useKV } from "@/hooks/use-kv-adapter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OffboardingProcess } from "@/types/lifecycle";
import { SignOut, CheckCircle, Clock, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export function OffboardingTasks() {
  const [offboardings, setOffboardings] = useKV<OffboardingProcess[]>("lifecycle-offboarding", [
    {
      id: "off-1",
      employeeId: "emp-202",
      employeeName: "Bob Smith",
      lastWorkingDay: "2026-01-31",
      status: "In Progress",
      progress: 60,
      reason: "Resignation",
      tasks: [
        { id: "t1", title: "Exit Interview", assignedTo: "HR", dueDate: "2026-01-25", status: "Completed", category: "HR" },
        { id: "t2", title: "Revoke Access", assignedTo: "IT", dueDate: "2026-01-31", status: "Pending", category: "IT" },
        { id: "t3", title: "Return Laptop", assignedTo: "emp-202", dueDate: "2026-01-31", status: "Pending", category: "Admin" },
      ]
    }
  ]);

  const handleCompleteTask = (processId: string, taskId: string) => {
    const updated = offboardings.map(proc => {
      if (proc.id === processId) {
        const newTasks = proc.tasks.map(t => t.id === taskId ? { ...t, status: 'Completed' } as any : t);
        const progress = Math.round((newTasks.filter(t => t.status === 'Completed').length / newTasks.length) * 100);
        return { ...proc, tasks: newTasks, progress };
      }
      return proc;
    });
    setOffboardings(updated);
    toast.success("Task marked as completed");
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Exit Processes</h3>
        <Button variant="destructive">
          <SignOut className="mr-2 h-4 w-4" /> Initiate Offboarding
        </Button>
      </div>

      <div className="grid gap-4">
        {offboardings.map((process) => (
          <Card key={process.id} className="border-l-4 border-l-red-500">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>{process.employeeName}</CardTitle>
                  <CardDescription>Last Day: {new Date(process.lastWorkingDay).toLocaleDateString()} • {process.reason}</CardDescription>
                </div>
                <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50">
                  {process.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Progress</span>
                    <span>{process.progress}%</span>
                  </div>
                  <Progress value={process.progress} className="bg-red-100" />
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Exit Checklist</h4>
                  {process.tasks.map(task => (
                    <div key={task.id} className="flex items-center justify-between p-2 border rounded bg-slate-50 dark:bg-slate-900/50">
                      <div className="flex items-center gap-2">
                        {task.status === 'Completed' ? (
                          <CheckCircle className="text-green-500 w-5 h-5" />
                        ) : (
                          <Warning className="text-red-500 w-5 h-5" />
                        )}
                        <span className={task.status === 'Completed' ? 'line-through text-slate-400' : ''}>
                          {task.title}
                        </span>
                        <Badge variant="outline" className="text-xs">{task.category}</Badge>
                      </div>
                      {task.status !== 'Completed' && (
                        <Button size="sm" variant="ghost" onClick={() => handleCompleteTask(process.id, task.id)}>
                          Mark Done
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
